import { useState, useEffect, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from './AuthContext';
import { AuthService } from './service/Auth.service';
import { AuthFormData, AuthState } from './dto/Auth.dto';
import { environment } from '../../shared/enviroment/environment';
import './styles/AuthPage.css';
import Header from '../index/components/Header/Header';
import Footer from '@/shared/components/footer/footer';

const authService = new AuthService(environment.api.apiRootServer);

const ErrorMessage = memo(({ error }: { error: string | null }) => (
  error ? (
    <div className="error-message">
      <AlertCircle />
      <span>{error}</span>
    </div>
  ) : null
));

const Spinner = memo(({ text }: { text: string }) => (
  <span>
    <div className="loading-spinner"></div>
    {text}
  </span>
));

const OtpInputs = memo(({ value, onChange, disabled }: { value: string, onChange: (idx: number, val: string) => void, disabled: boolean }) => (
  <div className="otp-container">
    {[0, 1, 2, 3].map((index) => (
      <input
        key={index}
        type="text"
        maxLength={1}
        className="otp-input"
        onChange={e => onChange(index, e.target.value)}
        data-index={index}
        value={value[index] || ''}
        disabled={disabled}
      />
    ))}
  </div>
));

const Terms = memo(() => (
  <p className="terms-text">
    By continuing, you agree to our{' '}
    <span className="terms-link" onClick={() => window.open('/terms', '_blank')}>Terms of Service</span>
    {' '}and{' '}
    <span className="terms-link" onClick={() => window.open('/privacy', '_blank')}>Privacy Policy</span>
  </p>
));


export default function AuthPage() {
  const [formData, setFormData] = useState<AuthFormData>({ phone: '', otp: '', name: '' });
  const [authState, setAuthState] = useState<AuthState>({ isLogin: true, otpSent: false, isLoading: false, error: null, resendCooldown: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      const fromCheckout = sessionStorage.getItem('fromCheckout') === 'true' || new URLSearchParams(location.search).get('fromCheckout') === 'true';
      const fromCart = sessionStorage.getItem('fromCart') === 'true';
      
      let redirectPath = '/';
      if (fromCheckout) {
        sessionStorage.removeItem('fromCheckout');
        redirectPath = '/checkout';
      } else if (fromCart) {
        sessionStorage.removeItem('fromCart');
        redirectPath = '/cart';
      }
      
      // Replace current history entry to prevent going back to auth page
      window.history.replaceState({}, document.title, redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location.search]);

  useEffect(() => {    // const params = new URLSearchParams(window.location.search);
    // const token = params.get('token');
    // if (token) {
    //   authService.getUserProfile(token)
    //     .then(data => {
    //       if (data.success && data.user) {
    //      } else {
    //           navigate('/', { replace: true });
    //         }
    //       }
    //     })
    //     .catch(() => { navigate('/auth', { replace: true }); });
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps        localStorage.setItem('userData', JSON.stringify(data.user));
    //         login(token, data.user);
    //         const fromCheckout = sessionStorage.getItem('fromCheckout') === 'true';
    //         if (fromCheckout) {
    //           sessionStorage.removeItem('fromCheckout');
    //           navigate('/checkout', { replace: true });
    //    
  }, []);

  useEffect(() => {
    if (authState.resendCooldown > 0) {
      const timer = setInterval(() => {
        setAuthState(prev => ({ ...prev, resendCooldown: prev.resendCooldown - 1 }));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [authState.resendCooldown]);

  if (authLoading) {
    return (
      <div className="page-loading">
        <div className="page-loading-spinner" />
      </div>
    );
  }
  if (isAuthenticated) return null;

  // DRY helpers
  // Robust phone normalization to match backend
  const cleanPhoneNumber = (phone: string) => {
    if (!phone) return '';
    let cleaned = phone.toString().trim().replace(/[\s\-\(\)\.]/g, '');
    cleaned = cleaned.replace(/^\+/, '');
    // Keep only non-digit removal, preserve country code if present
    if (cleaned.length === 10) {
      return '+91' + cleaned;
    } else if (cleaned.length === 12 && cleaned.startsWith('91')) {
      return '+' + cleaned;
    } else if (cleaned.length > 10) {
      return '+' + cleaned;
    }
    return cleaned;
  };
  const validatePhoneNumber = (phone: string) => phone.replace(/\D/g, '').length >= 10;

  // DRY event handlers
  const setError = (msg: string | null) => setAuthState(prev => ({ ...prev, error: msg }));
  const setLoading = (loading: boolean) => setAuthState(prev => ({ ...prev, isLoading: loading }));

  const handleSendOtp = async () => {
    setError(null);
    if (!formData.phone || !validatePhoneNumber(formData.phone)) return setError('Please enter a valid phone number');
    setLoading(true);
    try {
      const cleanPhone = cleanPhoneNumber(formData.phone);
      console.log('Sending OTP to:', cleanPhone);
      
      const response = await authService.sendUnifiedOtp({ phone: cleanPhone });
      if (response.success) {
        setFormData(prev => ({ ...prev, phone: cleanPhone }));
        console.log('OTP sent successfully');
        setAuthState(prev => ({ ...prev, otpSent: true, error: null, resendCooldown: 30 }));
      } else {
        setError(response.message || 'Failed to send OTP');
      }
    } catch (error: any) {
      console.error('OTP send failed:', error);
      setError(error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError(null);
    setLoading(true);
    if (!formData.otp || formData.otp.length !== 4) {
      setLoading(false);
      return setError('Please enter a valid 4-digit OTP');
    }
    
    try {
      console.log('Verifying OTP...');
      
      const response = await authService.verifyUnifiedOtp({
        phone: formData.phone,
        otp: formData.otp,
      });
      
      if (response.success && 'token' in response && 'user' in response) {
        login(response.token!, response.user!);
        console.log('Login successful');
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      setError(error.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (authState.resendCooldown > 0) return;
    setError(null); setLoading(true);
    try {
      console.log('Resending OTP to:', formData.phone);
      
      const response = await authService.sendUnifiedOtp({ phone: formData.phone });
      if (response.success) {
        console.log('OTP resent successfully');
        setAuthState(prev => ({ ...prev, resendCooldown: 30, error: null }));
      } else {
        setError(response.message || 'Failed to resend OTP');
      }
    } catch (error: any) {
      console.error('OTP resend failed:', error);
      setError(error.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };


  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = formData.otp.split('');
    newOtp[index] = value;
    setFormData(prev => ({ ...prev, otp: newOtp.join('') }));
    if (value && index < 3) {
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`) as HTMLInputElement;
      nextInput?.focus();
    }
  };

  // DRY main render
  return (
    <div className="auth-container">
      <Header />
      
      <main className="auth-main">
        <div className="auth-card auth-grid">
          <aside className="auth-left">
            <div className="auth-left-content">
              <img src="https://ik.imagekit.io/s4n0ittqy/kk%20-%20Copy.png" alt="Login" className="auth-left-image" />
            </div>
          </aside>
          <section className="auth-right">
            <div className="auth-right-header">
              <h2 className="auth-title">{authState.otpSent ? 'Verify Your Phone' : 'Login To Your Account'}</h2>
            </div>
            <div className="auth-form">
            {!authState.otpSent ? (
              <>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number</label>
                  <div className="phone-input-container">
                    <div className="country-code"><span>+91</span></div>
                    <input type="tel" id="phone" value={formData.phone} onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))} className="phone-input" placeholder="Enter your phone number" disabled={authState.isLoading} />
                  </div>
                </div>
                <ErrorMessage error={authState.error} />
                <button onClick={handleSendOtp} disabled={authState.isLoading} className="btn btn-primary">
                  {authState.isLoading ? <Spinner text="Sending OTP..." /> : 'Continue'}
                </button>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Enter verification code</label>
                  <OtpInputs value={formData.otp} onChange={handleOtpChange} disabled={authState.isLoading} />
                </div>
                <ErrorMessage error={authState.error} />
                <div className="form-group-resend">
                  <button onClick={handleResendOtp} disabled={authState.resendCooldown > 0 || authState.isLoading} className="resend-button">
                    {authState.resendCooldown > 0 ? `Resend code in ${authState.resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
                <div className="form-group-verify">
                  <button onClick={handleVerifyOtp} disabled={authState.isLoading || formData.otp.length !== 4} className="btn btn-primary">
                    {authState.isLoading ? <Spinner text="Verifying..." /> : 'Verify OTP'}
                  </button>
                </div>
              </>
            )}
            <Terms />
            </div>
          </section>
        </div>
      </main>
      <div className="auth-footer">
        <Footer />
      </div>
      <div id="recaptcha-container"></div>
    </div>
  );
}